import { G } from '@mobily/ts-belt';
import { GridRenderCellParams } from '@mui/x-data-grid';
// eslint-disable-next-line import-x/extensions -- it tries and fails importing ESM otherwise
import { read, utils } from 'xlsx/dist/xlsx.full.min.js';

import { PostB2bWithdrawalBulkPositionsEndpoint } from '@Common/types/bo/b2b/withdrawals';

type UploadedFileValue = {
  rawFile?: File;
};

type BulkReviewRow = {
  employee_id: number | null;
  nir: string;
  ntt: string;
  firstname: string;
  lastname: string;
  plan: string;
  instrument_name: string;
  quantity_available: number;
  quantity_to_withdraw: number;
  amount_available: number;
  is_deceased?: boolean;
};

// Bulk rows cannot carry a third-party bank account, so deceased employees must be withdrawn individually.
export const getDeceasedRowsToWithdraw = (rows: BulkReviewRow[] | undefined): BulkReviewRow[] =>
  (rows ?? []).filter((row) => row.is_deceased === true && row.quantity_to_withdraw > 0);

export type FormValues = {
  b2b_company_id: number;
  instrument_id: number;
  bulk_file: UploadedFileValue;
  bulk_file_key?: string;
  bulk_rows: BulkReviewRow[];
  bulk_rows_all_pages_visited?: boolean;
  bulk_rows_all_data_fetched?: boolean;
  // True when the company is the payout target but has no beneficiary configured.
  bulk_payout_beneficiary_missing?: boolean;
};

export type BulkPositionItem =
  PostB2bWithdrawalBulkPositionsEndpoint['Client']['ResponseBody']['positions'][number];

export type BulkReviewGridRow = BulkReviewRow & { id: number };

export const getGridRow = (params: GridRenderCellParams): BulkReviewGridRow =>
  params.row as BulkReviewGridRow;

const getTextValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }

  return '';
};

const getCellValue = (row: unknown[], index: number): unknown => {
  if (index < 0) {
    return null;
  }

  return row[index] ?? null;
};

const getNumericValue = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const normalized = value.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseBulkFileRows = async (file: File): Promise<BulkReviewRow[]> => {
  const fileBuffer = await file.arrayBuffer();
  const workbook = read(fileBuffer, { type: 'array' });
  const [firstSheetName] = workbook.SheetNames;

  if (G.isNullable(firstSheetName)) {
    return [];
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = utils.sheet_to_json<unknown[]>(worksheet, { header: 1, raw: false });

  if (rows.length === 0) {
    return [];
  }

  const headerRowIndex = rows.findIndex((row, index) => {
    if (index === 0) {
      return false;
    }

    return row.filter((value) => getTextValue(value) !== '').length >= 3;
  });

  if (headerRowIndex < 0) {
    return [];
  }

  // ['NIR*', 'NTT', 'Prénom', 'Nom', 'Dispositif', 'Support de placement',
  //  'Nombre total de parts', 'Valeur liquidative', 'Date de valeur', 'Encours total',
  //  'Type de retrait', 'Nombre de parts à racheter']
  const columnIndexes = {
    nir: 0,
    ntt: 1,
    firstname: 2,
    lastname: 3,
    plan: 4,
    instrument_name: 5,
    quantity_available: 6,
    quantity_to_withdraw: 11,
  } as const;

  const dataRows = rows.slice(headerRowIndex + 1);

  return dataRows
    .map<BulkReviewRow | null>((row) => {
      const nir = getTextValue(getCellValue(row, columnIndexes.nir));
      const ntt = getTextValue(getCellValue(row, columnIndexes.ntt));
      const firstname = getTextValue(getCellValue(row, columnIndexes.firstname));
      const lastname = getTextValue(getCellValue(row, columnIndexes.lastname));
      const quantity_to_withdraw = getNumericValue(
        getCellValue(row, columnIndexes.quantity_to_withdraw),
      );

      const hasData =
        nir.length > 0 ||
        ntt.length > 0 ||
        firstname.length > 0 ||
        lastname.length > 0 ||
        quantity_to_withdraw > 0;

      if (!hasData) {
        return null;
      }

      return {
        employee_id: null,
        nir,
        ntt,
        firstname,
        lastname,
        plan: getTextValue(getCellValue(row, columnIndexes.plan)),
        instrument_name: getTextValue(getCellValue(row, columnIndexes.instrument_name)),
        quantity_available: getNumericValue(getCellValue(row, columnIndexes.quantity_available)),
        quantity_to_withdraw,
        amount_available: 0,
      };
    })
    .filter((row): row is BulkReviewRow => row !== null);
};
